import { useCallback, useMemo } from 'react';
import { KEYS } from './ActionButtons/constants';
import { createSyncCheckHandler } from './actions/syncCheck';
import { useRepostPayment } from './actions/repostPayment';
import { createLaunchExternalAppHandler } from './actions/launchExternalApp';
import useVoidCheck from './actions/voidCheck';
import useTableTransfer from './actions/tableTransfer';
import {
  createDirectPrintHandler,
  createPrintHandler,
  usePrintModal,
} from './actions/print';
import useMergeCheck from './actions/mergeCheck';
import useAttachToParty from './actions/attachToParty';
import useGiftCard from './actions/giftCard';
import useGameCard from './actions/gameCard';
import useAddLoyalty from './actions/addLoyalty';
import useEmployeeTransfer from './actions/employeeTransfer';
import useReceipt from './actions/receipt';

const useMoreActionsController = ({
  data,
  authId,
  authInfo,
  deviceInfo,
  userInfo,
  serverAppTheme,
  closeTools,
  syncCheckAction,
  setErrorPopUp,
  printingAction,
  setNotification,
  setPrintNotification,
}) => {
  const sharedContext = useMemo(
    () => ({
      authId,
      authInfo,
      deviceInfo,
      userInfo,
      serverAppTheme,
      setErrorPopUp,
      checkId: data?.id,
      checkData: data,
      onClose: closeTools,
      syncCheck: syncCheckAction,
    }),
    [
      authId,
      authInfo,
      data,
      closeTools,
      setErrorPopUp,
      syncCheckAction,
      deviceInfo,
      userInfo,
      serverAppTheme,
    ]
  );
  const repost = useRepostPayment(sharedContext);
  const voidCheck = useVoidCheck(sharedContext);
  const tableTransfer = useTableTransfer(sharedContext);
  const mergeCheck = useMergeCheck(sharedContext);
  const attachToParty = useAttachToParty(sharedContext);
  const giftCard = useGiftCard({ checkData: data });
  const gameCard = useGameCard({ checkData: data });
  const addLoyalty = useAddLoyalty({ checkData: data });
  const employeeTransfer = useEmployeeTransfer(sharedContext);
  const receipt = useReceipt({ checkData: data, serverAppTheme });
  const printModal = usePrintModal({ row: data, serverAppTheme });
  const onDirectPrint = useMemo(
    () =>
      createDirectPrintHandler({
        data,
        deviceInfo,
        userInfo,
        printingAction,
        setErrorPopUp,
        setNotification,
        setPrintNotification,
      }),
    [
      data,
      deviceInfo,
      printingAction,
      setErrorPopUp,
      setNotification,
      setPrintNotification,
      userInfo,
    ]
  );
  const onPrint = useMemo(
    () =>
      createPrintHandler({
        onOpenPrint: printModal.onOpenPrint,
        onDirectPrint,
      }),
    [printModal.onOpenPrint, onDirectPrint]
  );

  const handlers = useMemo(
    () => ({
      [KEYS.SYNC_CHECK]: createSyncCheckHandler(sharedContext),
      [KEYS.REPOST_PAYMENT]: repost.open,
      [KEYS.LAUNCH_EXTERNAL_APP]: createLaunchExternalAppHandler(sharedContext),
      [KEYS.VOID_CHECK]: voidCheck.open,
      [KEYS.TABLE]: tableTransfer.open,
      [KEYS.EMPLOYEE]: employeeTransfer.open,
      [KEYS.MERGE_CHECK]: mergeCheck.open,
      [KEYS.ATTACH_TO_PARTY]: attachToParty.open,
      [KEYS.GIFT_CARD]: giftCard.open,
      [KEYS.GAME_CARD]: gameCard.open,
      [KEYS.ADD_LOYALTY]: addLoyalty.open,
      [KEYS.RECEIPT]: receipt.open,
      [KEYS.PRINT]: onPrint,
    }),
    [sharedContext, repost.open, voidCheck.open, tableTransfer.open, employeeTransfer.open, mergeCheck.open, attachToParty.open, giftCard.open, gameCard.open, addLoyalty.open, receipt.open, onPrint]
  );

  const onActionClick = useCallback(
    key => handlers[key]?.(),
    [handlers]
  );

  const modalsJSX = useMemo(
    () => (
      <>
        {repost.jsx}
        {voidCheck.jsx}
        {printModal.jsx}
        {tableTransfer.jsx}
        {employeeTransfer.jsx}
        {mergeCheck.jsx}
        {attachToParty.jsx}
        {addLoyalty.jsx}
        {receipt.jsx}
      </>
    ),
    [repost.jsx, voidCheck.jsx, printModal.jsx, tableTransfer.jsx, employeeTransfer.jsx, mergeCheck.jsx, attachToParty.jsx, addLoyalty.jsx, receipt.jsx]
  );

  return { onActionClick, modalsJSX };
};

export default useMoreActionsController;
